import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

/**
 * URL base de la API. Prioriza VITE_API_URL; si no, deriva de BASE_URL (+ "api/").
 */
function getApiBaseUrl(): string {
  const viteUrl = import.meta.env.VITE_API_URL as string | undefined;
  if (viteUrl && viteUrl !== 'undefined' && viteUrl !== 'null' && viteUrl !== '') {
    return viteUrl.endsWith('/') ? viteUrl : `${viteUrl}/`;
  }
  const base = (import.meta as any).env?.BASE_URL || '/';
  const normalized = base.endsWith('/') ? base : `${base}/`;
  return `${normalized}api/`;
}

const api = axios.create({
  baseURL: getApiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

/** Lee el token CSRF de la cookie que Django expone (CSRF_COOKIE_HTTPONLY = False). */
function getCsrfToken(): string | null {
  const name = 'csrftoken';
  if (!document.cookie) return null;
  for (const raw of document.cookie.split(';')) {
    const cookie = raw.trim();
    if (cookie.startsWith(name + '=')) {
      return decodeURIComponent(cookie.substring(name.length + 1));
    }
  }
  return null;
}

api.interceptors.request.use((config) => {
  const csrfToken = getCsrfToken();
  if (csrfToken) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  // Con FormData (subir documento/foto) hay que dejar que el navegador ponga
  // `multipart/form-data; boundary=…`; si forzamos application/json, el backend
  // no recibe el archivo.
  if (config.data instanceof FormData) {
    config.headers.delete('Content-Type');
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url;
    // 401 es «no hay sesión» (403 es «no tienes permiso»). En `me/` y `login/` es la
    // respuesta esperada y cada uno la maneja; en el resto, se cerró la sesión.
    if (status === 401 && !/^\/?(me|login|logout)\/?$/.test(url ?? '')) {
      useAuthStore.getState().marcarSesionExpirada();
    }
    if (![401, 403, 404].includes(status)) {
      console.error('❌ API Error:', { status, url, message: error.message });
    }
    return Promise.reject(error);
  }
);

export default api;
